import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/conversations');
      return response.data.data.data; // Access the actual conversations array
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/messages`);
      return { conversationId, messages: response.data.data.data }; // Access the actual messages array
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async ({ conversationId, content, type = 'text' }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/messages`, { message: content, type });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createConversation = createAsyncThunk(
  'messages/createConversation',
  async (participantId, { rejectWithValue }) => {
    try {
      const response = await api.post('/conversations', { participant_id: participantId });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  'messages/markMessagesAsRead',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/read`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const pinConversation = createAsyncThunk(
  'messages/pinConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/pin`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unpinConversation = createAsyncThunk(
  'messages/unpinConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/unpin`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const archiveConversation = createAsyncThunk(
  'messages/archiveConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/archive`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unarchiveConversation = createAsyncThunk(
  'messages/unarchiveConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/unarchive`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const addToFavorites = createAsyncThunk(
  'messages/addToFavorites',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/favorite`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const removeFromFavorites = createAsyncThunk(
  'messages/removeFromFavorites',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/unfavorite`);
      return { conversationId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const blockUser = createAsyncThunk(
  'messages/blockUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/users/${userId}/block`);
      return { userId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unblockUser = createAsyncThunk(
  'messages/unblockUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/users/${userId}/unblock`);
      return { userId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const reportUser = createAsyncThunk(
  'messages/reportUser',
  async ({ userId, reason, details }, { rejectWithValue }) => {
    try {
      const response = await api.post('/reports', { reported_user_id: userId, reason, details });
      return { userId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);


const initialState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  error: null,
  unreadCount: 0,
  typingUsers: {}, // { conversationId: [userIds] }
  favorites: [],
  blockedUsers: [],
  doNotDisturb: false,
  messageStatus: null,
  streaks: {}, // { conversationId: days }
};


const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    addMessage: (state, action) => {
      const { message, user } = action.payload;
      state.messages.push(message);

      // Update conversation's last message
      const conversation = state.conversations.find(conv => conv.id === message.conversation_id);
      if (conversation) {
        conversation.last_message = message;
        conversation.updated_at = message.created_at;

        // Increment unread count if message is from other user
        if (message.sender_id !== user?.id) {
          conversation.unread_count = (conversation.unread_count || 0) + 1;
        }
      }
      
      // Recalculate total unread count from all conversations
      state.unreadCount = state.conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
    },

    clearMessagesError: (state) => {
      state.error = null;
    },
    updateUnreadCount: (state) => {
      state.unreadCount = state.conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
    },
    updateConversation: (state, action) => {
      const { id, ...updates } = action.payload;
      const conversation = state.conversations.find(conv => conv.id === id);
      if (conversation) {
        Object.assign(conversation, updates);
      }
    },
    toggleFavorite: (state, action) => {
      const conversation = state.conversations.find(conv => conv.id === action.payload);
      if (conversation) {
        conversation.is_favorite = !conversation.is_favorite;
      }
    },
    setDoNotDisturb: (state, action) => {
      state.doNotDisturb = action.payload;
    },
    setTypingUser: (state, action) => {
      const { conversationId, userId, isTyping } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }
      if (isTyping) {
        if (!state.typingUsers[conversationId].includes(userId)) {
          state.typingUsers[conversationId].push(userId);
        }
      } else {
        state.typingUsers[conversationId] = state.typingUsers[conversationId].filter(id => id !== userId);
      }
    },
    addReaction: (state, action) => {
      const { messageId, reaction } = action.payload;
      const message = state.messages.find(m => m.id === messageId);
      if (message) {
        if (!message.reactions) message.reactions = [];
        message.reactions.push(reaction);
      }
    },
    removeReaction: (state, action) => {
      const { messageId, userId } = action.payload;
      const message = state.messages.find(m => m.id === messageId);
      if (message && message.reactions) {
        message.reactions = message.reactions.filter(r => r.user_id !== userId);
      }
    },
    updateMessageStatus: (state, action) => {
      state.messageStatus = action.payload;
    },
    updateStreak: (state, action) => {
      const { conversationId, days } = action.payload;
      state.streaks[conversationId] = days;
    },
    deleteMessage: (state, action) => {
      const messageId = action.payload;
      state.messages = state.messages.filter(m => m.id !== messageId);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
        state.unreadCount = action.payload.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messages = action.payload.messages;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        // action.payload contains { message: {...}, diamant_info: {...} }
        const message = action.payload.message || action.payload;
        state.messages.push(message);
        // Update conversation's last message
        const conversation = state.conversations.find(conv => conv.id === message.conversation_id);
        if (conversation) {
          conversation.last_message = message;
          conversation.updated_at = message.created_at;
        }
      })

      .addCase(createConversation.fulfilled, (state, action) => {
        state.conversations.unshift(action.payload);
        state.currentConversation = action.payload;
      })
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        // Mark messages as read in current conversation
        state.messages.forEach(message => {
          if (message.conversation_id === action.payload.conversationId && !message.is_read) {
            message.is_read = true;
          }
        });
        // Update conversation unread count
        const conversation = state.conversations.find(conv => conv.id === action.payload.conversationId);
        if (conversation) {
          conversation.unread_count = 0;
        }
        // Recalculate total unread count
        state.unreadCount = state.conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
      })
      .addCase(addToFavorites.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        const conversation = state.conversations.find(conv => conv.id === conversationId);
        if (conversation) {
          conversation.is_favorite = true;
        }
        if (!state.favorites.includes(conversationId)) {
          state.favorites.push(conversationId);
        }
      })
      .addCase(removeFromFavorites.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        const conversation = state.conversations.find(conv => conv.id === conversationId);
        if (conversation) {
          conversation.is_favorite = false;
        }
        state.favorites = state.favorites.filter(id => id !== conversationId);
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        const { userId } = action.payload;
        if (!state.blockedUsers.includes(userId)) {
          state.blockedUsers.push(userId);
        }
        // Remove conversations with blocked user
        state.conversations = state.conversations.filter(conv => {
          const otherUser = conv.participants?.find(p => p.id !== userId);
          return !otherUser || otherUser.id !== userId;
        });
      })
      .addCase(unblockUser.fulfilled, (state, action) => {
        const { userId } = action.payload;
        state.blockedUsers = state.blockedUsers.filter(id => id !== userId);
      });
  },

});

export const { 
  setCurrentConversation, 
  addMessage, 
  clearMessagesError, 
  updateUnreadCount, 
  updateConversation, 
  toggleFavorite, 
  setDoNotDisturb,
  setTypingUser,
  addReaction,
  removeReaction,
  updateMessageStatus,
  updateStreak,
  deleteMessage
} = messagesSlice.actions;

export default messagesSlice.reducer;
